import { Building2, LayoutDashboard, LogIn, LogOut, Menu, UserRound, X } from 'lucide-react'
import { useState } from 'react'
import { Link, useLocation } from 'react-router'
import { signOut } from '../../lib/auth.client'
import { Button } from '../ui'
import { Box, Container, HStack, VStack, styled } from '../../styled-system/jsx'

interface SiteHeaderProps {
    readonly user: {
        readonly name: string
        readonly email: string
    } | null
}

const HeaderLink = styled(Link)

export function SiteHeader({ user }: SiteHeaderProps) {
    const [mobileOpen, setMobileOpen] = useState(false)
    const location = useLocation()
    const dashboardActive = location.pathname === '/dashboard'
    const venuesActive = location.pathname === '/venues' || location.pathname.startsWith('/venues/')

    const handleSignOut = () => {
        void signOut().then(() => {
            window.location.href = '/login'
        })
    }

    return (
        <styled.header bg="bg.header" borderBottomWidth="1px" borderColor="border.subtle" color="fg.default">
            <Container maxW="7xl" px={{ base: '4', md: '6' }}>
                <HStack minH="16" justify="space-between" gap="5">
                    <HStack gap={{ base: '3', md: '4' }} minW="0">
                        <Link to={user ? '/dashboard' : '/venues/start'} aria-label="ENTX Venue Partners home">
                            <styled.img
                                src="/FINAL_entx_black.png"
                                alt="ENTX"
                                className="brand-logo-on-dark"
                                h={{ base: '8', md: '9' }}
                                w="auto"
                            />
                        </Link>
                        <Box h="7" borderLeftWidth="1px" borderColor="border.default" />
                        <VStack alignItems="start" gap="0" minW="0">
                            <styled.span fontSize="xs" color="fg.subtle" textTransform="uppercase">
                                Self-Service
                            </styled.span>
                            <styled.span fontSize="sm" fontWeight="semibold" whiteSpace="nowrap">
                                Venue partners
                            </styled.span>
                        </VStack>
                    </HStack>

                    <HStack display={{ base: 'none', md: 'flex' }} gap="2">
                        {user ? (
                            <>
                                <HeaderLink
                                    to="/dashboard"
                                    display="flex"
                                    alignItems="center"
                                    gap="2"
                                    px="3"
                                    py="2"
                                    borderRadius="md"
                                    fontSize="sm"
                                    fontWeight="medium"
                                    color={dashboardActive ? 'fg.default' : 'fg.muted'}
                                    bg={dashboardActive ? 'bg.muted' : 'transparent'}
                                    textDecoration="none"
                                    _hover={{ bg: 'bg.muted', color: 'fg.default' }}
                                >
                                    <LayoutDashboard size={17} aria-hidden="true" />
                                    Dashboard
                                </HeaderLink>
                                <HeaderLink
                                    to="/venues"
                                    display="flex"
                                    alignItems="center"
                                    gap="2"
                                    px="3"
                                    py="2"
                                    borderRadius="md"
                                    fontSize="sm"
                                    fontWeight="medium"
                                    color={venuesActive ? 'fg.default' : 'fg.muted'}
                                    bg={venuesActive ? 'bg.muted' : 'transparent'}
                                    textDecoration="none"
                                    _hover={{ bg: 'bg.muted', color: 'fg.default' }}
                                >
                                    <Building2 size={17} aria-hidden="true" />
                                    Venues
                                </HeaderLink>
                                <HStack gap="2" pl="3" ml="1" borderLeftWidth="1px" borderColor="border.subtle">
                                    <UserRound size={17} aria-hidden="true" />
                                    <VStack alignItems="start" gap="0" maxW="44">
                                        <styled.span fontSize="sm" fontWeight="medium" truncate>{user.name}</styled.span>
                                        <styled.span fontSize="xs" color="fg.subtle" truncate>{user.email}</styled.span>
                                    </VStack>
                                </HStack>
                                <Button variant="plain" size="sm" onClick={handleSignOut}>
                                    <LogOut size={17} aria-hidden="true" />
                                    Sign out
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button asChild variant="plain" size="sm">
                                    <Link to="/login?intent=venue"><LogIn size={17} aria-hidden="true" />Sign in</Link>
                                </Button>
                                <Button asChild size="sm">
                                    <Link to="/venues/start"><Building2 size={17} aria-hidden="true" />List your venue</Link>
                                </Button>
                            </>
                        )}
                    </HStack>

                    <Button
                        display={{ base: 'inline-flex', md: 'none' }}
                        variant="plain"
                        size="sm"
                        aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'}
                        aria-expanded={mobileOpen}
                        onClick={() => setMobileOpen((open) => !open)}
                    >
                        {mobileOpen ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
                    </Button>
                </HStack>

                {mobileOpen && (
                    <VStack display={{ base: 'flex', md: 'none' }} alignItems="stretch" gap="2" pb="4">
                        {user ? (
                            <>
                                <Button asChild variant="plain" justifyContent="flex-start">
                                    <Link to="/dashboard" onClick={() => setMobileOpen(false)}>
                                        <LayoutDashboard size={17} aria-hidden="true" />Dashboard
                                    </Link>
                                </Button>
                                <Button asChild variant="plain" justifyContent="flex-start">
                                    <Link to="/venues" onClick={() => setMobileOpen(false)}>
                                        <Building2 size={17} aria-hidden="true" />Venues
                                    </Link>
                                </Button>
                                <HStack px="3" py="2" gap="2">
                                    <UserRound size={17} aria-hidden="true" />
                                    <styled.span fontSize="sm" color="fg.muted" truncate>{user.email}</styled.span>
                                </HStack>
                                <Button variant="outline" justifyContent="flex-start" onClick={handleSignOut}>
                                    <LogOut size={17} aria-hidden="true" />Sign out
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button asChild variant="outline" justifyContent="flex-start">
                                    <Link to="/login?intent=venue" onClick={() => setMobileOpen(false)}>
                                        <LogIn size={17} aria-hidden="true" />Sign in
                                    </Link>
                                </Button>
                                <Button asChild justifyContent="flex-start">
                                    <Link to="/venues/start" onClick={() => setMobileOpen(false)}>
                                        <Building2 size={17} aria-hidden="true" />List your venue
                                    </Link>
                                </Button>
                            </>
                        )}
                    </VStack>
                )}
            </Container>
        </styled.header>
    )
}
